import { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import moment from "moment"
import { useCalendarJournalNoteDates } from "../../../hooks/useCalendarSelectors"
import { useTimeOffForDate } from "../../../hooks/useTimeOff"
import { useRefreshCalendarBookings } from "../../../useRefreshCalendarBookings"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import { computeEventColumns, getLanesForDay, getDayMinWidth, HOUR_HEIGHT } from "../timeGridUtils"
import { UNASSIGNED_ID } from "../../../constants"

const getResourceLabel = (resource) => {
  if (resource === "unassigned_equipment" || resource === "unassigned_crew") return "Unassigned"
  return typeof resource === "string" ? resource : resource?.title || ""
}

const getResourceEvents = (dayEvents, resource, isEquipmentTab, isCrewTab, resourceId) => {
  if (isEquipmentTab) {
    if (resource === "unassigned_equipment") return dayEvents.filter((ev) => !ev.assigned_equipment || ev.assigned_equipment.length === 0)
    return dayEvents.filter((ev) =>
      (ev.assigned_equipment || []).length > 0 &&
      (ev.assigned_equipment || []).some((item) => item?.name === resource || item?.equipment_title === resource)
    )
  }
  if (isCrewTab) {
    if (resource === "unassigned_crew" || resource === "Unassigned") return dayEvents.filter((ev) => !ev.assigned_crew || ev.assigned_crew.length === 0)
    return dayEvents.filter((ev) => (ev.assigned_crew || []).some((item) => item?.name === resource || item?.full_name === resource))
  }
  if (resourceId) return dayEvents.filter((ev) => ev.experience_id === resourceId)
  return dayEvents.filter((ev) => ev.resource === getResourceLabel(resource))
}

const useDayGridData = ({ date, hours, events, resources: resourcesProp, profileIds, boatIds, activeTab, startHour, endHour }) => {
  const calendarCrew = useSelector((state) => state.staff?.calendarCrew || [])
  const equipmentList = useSelector((state) =>
    (state.calendar?.calendarEquipment || []).map((eq) => ({ id: eq.id, name: eq.equipment_title }))
  )
  const isCrewTab = activeTab === "crew"
  const isEquipmentTab = activeTab === "equipment"

  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)

  const journalNoteDates = useCalendarJournalNoteDates()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()
  const timeOffForDay = useTimeOffForDate(date)
  const refreshCalendarBookings = useRefreshCalendarBookings()

  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)
  const currentDay = moment(date).format("YYYY-MM-DD")
  const hasJournalNote = journalNoteDates?.includes(currentDay)
  const hasJournalPermission = hasJournalNote ? canViewJournal || canEditJournal : canCreateJournal

  const crewMembers = useMemo(() => {
    if (!isCrewTab) return []
    const active = calendarCrew.filter((c) => c.status?.toLowerCase() === "active")
    if (profileIds?.length > 0) {
      const ids = profileIds.filter((id) => id !== UNASSIGNED_ID && id !== "unassigned").map(String)
      return ids.length > 0 ? active.filter((c) => ids.includes(String(c.id))) : active
    }
    return active
  }, [calendarCrew, profileIds, isCrewTab])

  const normalizedDayEvents = useMemo(() => events.map((ev) => {
    const startMoment = ev.start ? moment(ev.start, ["h:mm A", "hh:mm A", "HH:mm"]) : null
    const endMoment = ev.end ? moment(ev.end, ["h:mm A", "hh:mm A", "HH:mm"]) : null
    return { ...ev, resource: ev.resource || ev.title || "Experience", startMoment, endMoment, startHour: startMoment?.isValid() ? startMoment.hour() : null, endHour: endMoment?.isValid() ? endMoment.hour() : null, startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start, endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end }
  }), [events])

  const dayEvents = useMemo(() => normalizedDayEvents.filter((ev) => {
    if (moment(ev.date).format("YYYY-MM-DD") !== currentDay) return false
    if (ev.startHour === null) return true
    return ev.startHour >= startHour && ev.startHour < endHour
  }), [normalizedDayEvents, currentDay, startHour, endHour])

  const resources = useMemo(() => {
    if (isCrewTab) {
      const hasCrewFilters = profileIds?.filter((id) => id !== UNASSIGNED_ID && id !== "unassigned")?.length > 0
      const showUnassigned = profileIds?.includes(UNASSIGNED_ID) || profileIds?.includes("unassigned") || !hasCrewFilters
      const crewRows = crewMembers.map((c) => c.name)
      return showUnassigned ? ["unassigned_crew", ...crewRows] : crewRows
    }
    if (isEquipmentTab) {
      const hasEquipmentFilters = boatIds?.filter((id) => id !== UNASSIGNED_ID && id !== "unassigned")?.length > 0
      const showUnassigned = boatIds?.includes(UNASSIGNED_ID) || boatIds?.includes("unassigned") || !hasEquipmentFilters
      return showUnassigned ? ["unassigned_equipment", ...equipmentList.map((eq) => eq.name)] : equipmentList.map((eq) => eq.name)
    }
    const explicit = (resourcesProp || []).filter(Boolean)
    if (explicit.length) {
      const seen = new Set()
      return explicit.filter((item) => {
        const key = item?.id ?? item
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    }
    const names = normalizedDayEvents.map((ev) => ev.resource).filter(Boolean)
    const unique = Array.from(new Set(names))
    return unique.length ? unique : ["Experience"]
  }, [normalizedDayEvents, resourcesProp, isCrewTab, isEquipmentTab, equipmentList, crewMembers, boatIds, profileIds])

  const totalHeight = hours.length * HOUR_HEIGHT

  const gridTemplateColumns = useMemo(() => {
    const cols = resources.map((resource) => {
      const resourceId = typeof resource === "string" ? null : resource?.id
      const resEvents = getResourceEvents(dayEvents, resource, isEquipmentTab, isCrewTab, resourceId)
      const valid = resEvents.filter((ev) => ev.startMoment?.isValid() && ev.endMoment?.isValid())
      const lanes = getLanesForDay(computeEventColumns(valid))
      return `minmax(${getDayMinWidth(lanes)}px, 1fr)`
    })
    return `52px 1px ${cols.join(" ")}`
  }, [resources, dayEvents, isEquipmentTab, isCrewTab])

  const handleBookingCancel = () => { setIsBookingModalOpen(false); setSelectedBookingId(null) }
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  return {
    isJournalModalOpen, setIsJournalModalOpen, isBookingModalOpen, setIsBookingModalOpen,
    selectedBookingId, setSelectedBookingId, currentDay, hasJournalPermission, journalNoteDates,
    active, timeOffForDay, dayEvents, resources, totalHeight, gridTemplateColumns,
    isCrewTab, isEquipmentTab, handleBookingCancel, handleBookingCancelSuccess,
    refreshCalendarBookings,
    getResourceLabel, getResourceEvents,
  }
}

export { getResourceLabel, getResourceEvents }
export default useDayGridData
